import GeoValidationDrawer from "components/GeoValidationDrawer";
import styles from "./AddressForm.module.scss";
import { Box, Button, Center, CheckIcon } from "@mantine/core";
import { phoneMask } from "helpers/masks";
import { FC, useEffect, useState } from "react";
import ReactSelect from "react-select";
import { store } from "stores/Store";
import useExternalApiService from "helpers/ExternalApiService";
import { useRegions } from "helpers/useRegionHook";

type GeoValidationAction = "validate" | "draft" | "";


const COUNTRIES = [
  { label: "Australia", value: "AU" },
  { label: "Canada", value: "CA" },
  { label: "United States", value: "US" },
  { label: "Puerto Rico", value: "PR" },
];

interface AddressForm {
  address1: string;
  address2: string;
  city: string;
  state: any; // Changed from string to any to accommodate react-select object
  zipcode: string;
  latLon?: [number, number];
  latitude?: number;
  longitude?: number;
  phone: string;
  is_residential: boolean;
  address?: string; //legacy
  country: any;
}

interface Props {
  practitionerId: string;
  onSave: () => void;
  addressId?: string;
  initialForm?: Partial<AddressForm>;
}

const AddressForm: FC<Props> = ({ practitionerId, addressId, initialForm, onSave }) => {
  const [form, setForm] = useState<AddressForm>({
    address1: initialForm?.address1 || initialForm?.address || "",
    address2: initialForm?.address2 || "",
    city: initialForm?.city || "",
    state: initialForm?.state ?
      { label: initialForm?.state, value: initialForm?.state } as any :
      { label:  "", value: "" } as any,
    zipcode: initialForm?.zipcode || "",
    latLon: initialForm?.latitude && initialForm?.longitude ? 
      [initialForm.latitude, initialForm.longitude] : undefined,
    phone: initialForm?.phone || "",
    is_residential: initialForm?.is_residential || false,
    country: initialForm?.country || "US",
  });
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { patchData, postData } = useExternalApiService(apiUrl);
  const [formState, setFormState] = useState<Partial<AddressForm>>({});
  const [geoValidationAction, setGeoValidationAction] = useState<GeoValidationAction>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isGeoValidated = !!form?.latLon;
  const isFormValid = Object.keys(formState).length === 0;
  const regions = useRegions(form.country);

  const handleGeoValidation = (action: GeoValidationAction = "validate") => {
    setGeoValidationAction(action);
  };

  const createAddress = async (f: AddressForm) => {
    setIsSubmitting(true);
    if (addressId) {
      await patchData(`/api/practitioner/${practitionerId}/address/${addressId}`, {
        address_data: {
          address: `${f.address1}\n${f.address2}\n${f?.city}, ${(f?.state as any)?.value} ${f?.zipcode}`,
          address1: `${f.address1}`,
          address2: `${f.address2}`,
          phone: f.phone,
          latitude: (f?.latLon ? f.latLon[0] : undefined),
          longitude: (f?.latLon ? f.latLon[1] : undefined),
          city: f.city,
          state: (f.state as any)?.value,
          zipcode: f.zipcode,
          country: f.country,
          is_residential: f.is_residential ? 1 : 0,
        }
      });
    } else {
      await postData(`/api/practitioner/${practitionerId}/address`, {
        address_data: {
          address: `${f.address1}\n${f.address2}\n${f?.city}, ${(f?.state as any)?.value} ${f?.zipcode}`,
          address1: `${f.address1}`,
          address2: `${f.address2}`,
          phone: f.phone,
          latitude: (f?.latLon ? f.latLon[0] : undefined),
          longitude: (f?.latLon ? f.latLon[1] : undefined),
          city: f.city,
          state: (f.state as any)?.value,
          zipcode: f.zipcode,
          country: f.country,
          is_residential: f.is_residential ? 1 : 0,
        }
      });
    }

    const response = await postData('/api/get_practitioner_by_id', {
      practitioner_id: practitionerId
    })
    store.clearPractitionersCache();
    store.setCurrentPractitioner(response.practitioner);
    setIsSubmitting(false);
    onSave();
  };

  useEffect(() => {
    const errObj: { [k: string]: string } = {};

    if (!form.address1) {
      errObj["address1"] = "empty";
    }

    if (!form.latLon) {
      errObj["latLon"] = "empty";
    }

    if (!form.state) {
      errObj["state"] = "empty";
    }

    if (!form.zipcode) {
      errObj["zipcode"] = "empty";
    }

    if (!form.city) {
      errObj["city"] = "empty";
    }

    if (!form.country) {
      errObj["country"] = "empty";
    }

    setFormState(errObj);
  }, [form]);

  return (
    <Box>
      <GeoValidationDrawer
        opened={!!geoValidationAction}
        onClose={() => setGeoValidationAction("")}
        addressToValidate={{
          administrativeArea: (form.state as any)?.value || "",
          locality: form.city || "",
          postalCode: form.zipcode || "",
          addressLines: [form.address1 || "", form.address2 || ""],
          regionCode: form.country || "",
        }}
        onConfirm={(a) => {
          if (a) {
            const f = {
              ...form,
              state: { label: a.administrativeArea, value: a.administrativeArea },
              city: a.locality,
              address1: a.addressLines[0],
              address2: a.addressLines[1],
              zipcode: a.postalCode,
              latLon: a.latLon,
              country: form.country,
            };
            setForm(f);
            if (geoValidationAction === "draft") {
              alert("create");
            }
          } else {
            const f = {
              ...form,
              latLon: undefined
            };
            setForm(f);
            if (geoValidationAction === "draft") {
              alert("create")
            }
          }
          setGeoValidationAction("");
        }}
      />
      <div className={styles["form__input"]}>
        <div className={styles["form__input-header"]}>
          <label className={styles["form__input-label"]}>
            Address<span className={styles.required}>*</span>
          </label>
          <label className={styles["form__checkbox-label"]}>
            <Button
              onClick={() => handleGeoValidation()}
              leftSection={isGeoValidated ? <CheckIcon size={10} /> : null}
              variant="transparent">
              Geo-Validate
            </Button>
          </label>
        </div>
        <input
          className={formState.address1 ? styles["form__input--error"] : ""}
          value={form.address1}
          placeholder="123 Wellness Road"
          onChange={(e) => setForm({ ...form, address1: e.target.value })}
          type="text" />
        <input
          className={formState.address2 ? styles["form__input--error"] : ""}
          value={form.address2}
          onChange={(e) => setForm({ ...form, address2: e.target.value })}
          placeholder="Suite 456"
          type="text" />
      </div>
      <div className={styles["form__input"]}>
        <label>
          Country<span className={styles.required}>*</span>
        </label>
        <ReactSelect
          value={COUNTRIES.find(country => country.value === form.country)}
          className={styles["form__input--select"]}
          onChange={(v: any) => setForm({ ...form, country: v.value })}
          placeholder="United States"
          styles={{
            control: (baseStyles: any) => ({
              ...baseStyles,
              border: 'none',
              borderBottom: '1px solid #ccc',
              paddingLeft: 0,
              marginLeft: 0,
              color: '#4F7D9C',
              boxShadow: 'none',
              minHeight: "30px",
              maxHeight: "30px",
              '&:hover': {
                boxShadow: 'none'
              },

            }),
            dropdownIndicator: (baseStyles: any) => ({
              ...baseStyles,
              marginTop: "-5px",
            }),
            valueContainer: (baseStyles: any) => ({
              ...baseStyles,
              paddingLeft: 0,
              marginLeft: 0,
            }),
            indicatorSeparator: () => ({
              display: 'none'
            })
          }}
          options={COUNTRIES.map((country) => ({
            label: country.label,
            value: country.value,
          })) as []} />
      </div>
      <div className={styles["form__input"]}>
        <label>
          {form.country === "AU" ? "Suburb" : "City"}<span className={styles.required}>*</span>
        </label>
        <input
          className={formState.city ? styles["form__input--error"] : ""}
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder="Palm Coast"
          type="text" />
      </div>
      <div className={styles["form__side-by-side"]}>
        <div className={styles["form__input"]}>
          <label>
            {form.country === "CA" ? "Province/State" : form.country === "AU" ? "State/Territory" : "State"}<span className={styles.required}>*</span>
          </label>
          <ReactSelect
            value={form.state}
            className={styles["form__input--select"]}
            onChange={(v: any) => setForm({ ...form, state: v })}
            placeholder="FL"
            menuPlacement="top"
            styles={{
              control: (baseStyles: any) => ({
                ...baseStyles,
                border: 'none',
                borderBottom: '1px solid #ccc',
                paddingLeft: 0,
                marginLeft: 0,
                color: '#4F7D9C',
                boxShadow: 'none',
                minHeight: "30px",
                maxHeight: "30px",
                '&:hover': {
                  boxShadow: 'none'
                },

              }),
              dropdownIndicator: (baseStyles: any) => ({
                ...baseStyles,
                marginTop: "-5px",
              }),
              valueContainer: (baseStyles: any) => ({
                ...baseStyles,
                paddingLeft: 0,
                marginLeft: 0,
              }),
              indicatorSeparator: () => ({
                display: 'none'
              })
            }}
            options={regions.map((s) => ({
              label: s.label,
              value: s.value,
            })) as []} />
        </div>
        <div className={styles["form__input"]}>
          <label>
            {form.country === "US" ? "Zipcode" : "Postal Code"}<span className={styles.required}>*</span>
          </label>
          <input
            className={formState.zipcode ? styles["form__input--error"] : ""}
            value={form.zipcode}
            onChange={(e) => setForm({ ...form, zipcode: e.target.value })}
            placeholder="01234"
            type="text"
          />
        </div>
      </div>
      <div className={styles["form__side-by-side"]}>
        <div className={styles["form__input"]}>
          <label>
            Phone
          </label>
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: phoneMask(e.target.value, form.country) })}
            placeholder="(888)888-8888"
            type="text" />
        </div>
        <div className={styles["form__input"]}>
          <label>
            Is Residential?
          </label>
          <input
            checked={form.is_residential}
            type="checkbox"
            onChange={(e) => setForm({ ...form, is_residential: !form.is_residential })} 
            />
        </div>
      </div>

      <Center mt="xl">
        <Button 
          loading={isSubmitting}
          disabled={!isFormValid} 
          onClick={() => createAddress(form)}>
          SAVE
        </Button>
      </Center>
    </Box>
  );
};

export default AddressForm;